<template>
  <v-menu
    v-if="!readonly"
    ref="menu"
    v-model="menu"
    :close-on-content-click="true"
    transition="scale-transition"
    offset-y
    min-width="18.125rem"
    content-class="v-menu-work-date-field"
    fixed
  >
    <template #activator="{ on: { click: openDatePicker } }">
      <w-text-field
        ref="textField"
        class="w-date-year-field"
        v-bind="$props"
        :value="displayValue"
        :clearable="isClearable"
        append-icon="$wCalendar"
        :error-messages="errorMessages"
        @change="handleTextFieldChange"
        @click:clear="handleClear"
        @click:append="e => customOpenDatePicker(e, openDatePicker)"
      />
    </template>
    <v-date-picker-years
      id="picker"
      ref="picker"
      v-model="pickerValue"
      no-title
      scrollable
      locale="de"
      :max="max"
      :min="min"
      @input="handlePickerInput"
    />
  </v-menu>
  <w-read-only-text-field
    v-else
    v-bind="$props"
    :value="formattedDate"
  />
</template>

<script>
// components
import { VTextField } from 'vuetify/lib';

// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';
import { TODAY, CURRENT_YEAR } from '@/services/date-service';

const RANGE = 50;
/**
 * @component WorkDateYearField
 * @description Year selection field with picker. Shows a list of selectable
 * years within a defined range. All Vuetify `VTextField` props are inherited.
 *
 * @prop {Boolean} [inlineReadOnly=false] - Display inline in read-only mode.
 * @prop {Boolean} [isClearable=true] - Show clear button.
 * @prop {String} [max] - Latest selectable year (default: current year +50).
 * @prop {String} [min] - Earliest selectable year (default: current year -50).
 *
 * @emits {String} input - Emitted when a year is selected.
 */
export default {
  name: 'WorkDateYearField',
  mixins: [isRequired, pendingAble],
  props: {
    ...VTextField.options.props,
    inlineReadOnly: { type: Boolean, default: false },
    isClearable: { type: Boolean, default: true },
    max: { type: String, default: String(new Date(TODAY).getFullYear() + RANGE) },
    min: { type: String, default: String(new Date(TODAY).getFullYear() - RANGE) }
  },
  data() {
    return {
      lazyValue: this.value,
      menu: false,
      pickerValue: null,
      textFieldValue: null,
    };
  },
  computed: {
    displayValue() {
      if (this.menu) {
        return this.textFieldValue !== null ? this.textFieldValue : this.lazyValue;
      }
      return this.lazyValue;
    },
    formattedDate() {
      return this.lazyValue;
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        if (val !== this.lazyValue) {
          this.lazyValue = val;
          this.$emit('input', val);
        }
      }
    }
  },
  methods: {
    focus() {
      this.$refs.textField.focus();
    },
    customOpenDatePicker(event, openDatePicker) {
      this.textFieldValue = this.lazyValue;
      this.pickerValue = this.lazyValue || CURRENT_YEAR;
      openDatePicker(event);
      this.$nextTick(() => {
        setTimeout(() => {
          const activeItem = document.querySelector("li.active");
          if (activeItem) {
            activeItem.scrollIntoView({
              behavior: "auto",
              block: "center",
              inline: "nearest",
            });
          }
        }, 300);
      });
    },
    handlePickerInput(selectedYear) {
      this.internalValue = selectedYear;
      this.$refs.menu.save(selectedYear);
    },
    handleTextFieldChange(newValue) {
      this.textFieldValue = newValue;
      this.internalValue = newValue === '' ? null : newValue;
    },
    handleClear() {
      this.textFieldValue = null;
      this.internalValue = null;
    }
  },
  watch: {
    value(val) {
      this.lazyValue = val;
    },
    menu(isOpen) {
      if (!isOpen) {
        this.textFieldValue = null;
      }
    }
  }
};
</script>

<style lang="scss">
.v-menu-work-date-field {
  color: $input-label-color !important;
}
  @media only screen and (max-width: 599.98px) {
    .v-menu__content {
      height: auto !important;
    }
    .v-picker {
      width: 100%;
    }
  }
</style>
